import {apolloClient} from '@/vue-apollo';

const STALE_MS = 5 * 60 * 1000;

export default {
  state: {
    byCompany: {},
  },
  mutations: {
    categoryConfig_request(state, companyId) {
      const existing = state.byCompany[companyId] || {data: [], lastFetched: null, requestId: 0};
      const requestId = existing.requestId + 1;
      state.byCompany = {
        ...state.byCompany,
        [companyId]: {...existing, loading: true, requestId},
      };
    },
    categoryConfig_success(state, {companyId, configs, requestId}) {
      const existing = state.byCompany[companyId] || {requestId: 0};
      if (requestId !== existing.requestId) {
        // A newer request has since been issued for this company; ignore this stale response.
        return;
      }
      state.byCompany = {
        ...state.byCompany,
        [companyId]: {...existing, data: configs, loading: false, lastFetched: Date.now()},
      };
    },
    categoryConfig_error(state, {companyId, requestId}) {
      const existing = state.byCompany[companyId] || {data: [], lastFetched: null, requestId: 0};
      if (requestId !== existing.requestId) {
        return;
      }
      state.byCompany = {
        ...state.byCompany,
        [companyId]: {...existing, loading: false},
      };
    },
    categoryConfig_upsert(state, {companyId, config}) {
      const existing = state.byCompany[companyId];
      if (!existing) {
        return;
      }
      const idx = existing.data.findIndex((c) => c.id === config.id);
      const data = [...existing.data];
      if (-1 === idx) {
        data.push(config);
      } else {
        data[idx] = config;
      }
      state.byCompany = {
        ...state.byCompany,
        [companyId]: {...existing, data},
      };
    },
  },
  actions: {
    fetchCategoryConfigs({commit, state}, companyId) {
      const entry = state.byCompany[companyId];
      if (entry && entry.loading) {
        return;
      }
      if (entry && entry.lastFetched && (Date.now() - entry.lastFetched) < STALE_MS) {
        return;
      }
      commit('categoryConfig_request', companyId);
      const requestId = state.byCompany[companyId].requestId;
      apolloClient.query({
        query: require('@/graphql/Query/Collection/CategoryConfigs.graphql'),
        variables: {company: companyId},
        fetchPolicy: 'no-cache',
      }).then((response) => {
        commit('categoryConfig_success', {companyId, configs: response.data.categoryConfigs, requestId});
      }).catch(() => {
        commit('categoryConfig_error', {companyId, requestId});
      });
    },
    upsertCategoryConfig({commit, state}, {companyId, config}) {
      if (!state.byCompany[companyId]) {
        return;
      }
      commit('categoryConfig_upsert', {companyId, config});
    },
    // Unlike fetchCategoryConfigs, this always issues a fresh request even if one is already
    // in flight - callers use it after a mutation (e.g. up-issue) to guarantee fresh data,
    // rather than risking a stale in-flight response silently winning the race.
    refetchCategoryConfigs({commit, state}, companyId) {
      commit('categoryConfig_request', companyId);
      const requestId = state.byCompany[companyId].requestId;
      apolloClient.query({
        query: require('@/graphql/Query/Collection/CategoryConfigs.graphql'),
        variables: {company: companyId},
        fetchPolicy: 'no-cache',
      }).then((response) => {
        commit('categoryConfig_success', {companyId, configs: response.data.categoryConfigs, requestId});
      }).catch(() => {
        commit('categoryConfig_error', {companyId, requestId});
      });
    },
  },
  getters: {
    categoryConfigsByCompany: (state) => (companyId) => {
      return (state.byCompany[companyId] || {}).data || [];
    },
    categoryConfigsForCompanyAndPeriod: (state) => (companyId, afterDate, beforeDate) => {
      const configs = (state.byCompany[companyId] || {}).data || [];
      if (!afterDate || !beforeDate) {
        return configs;
      }
      return configs.filter((config) => {
        const firstOk = !config.firstCycle || config.firstCycle.startDate <= beforeDate;
        const lastOk = !config.lastCycle || config.lastCycle.startDate >= afterDate;
        return firstOk && lastOk;
      });
    },
    categoryConfigsLoadingForCompany: (state) => (companyId) => {
      return !!(state.byCompany[companyId] || {}).loading;
    },
  },
};
